import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from '@/_utils/axios';
import { FocusScheduling, FocusVersionScheduling } from '@/redux/slices/slice_types';

type ApiStatus = 'idle' | 'loading' | 'success' | 'error';

type VersionSchedulingState = {
  rows: FocusVersionScheduling[];
  status: ApiStatus;
  error: string | null;
};

type IncrementalSchedulingState = {
  byVersionSchedulingId: Record<number, FocusScheduling[]>;
  statusByVersionSchedulingId: Record<number, ApiStatus>;
  errorByVersionSchedulingId: Record<number, string | null>;
  deleteStatusBySchedulingId: Record<number, ApiStatus>;
};

type FocusVersionSchedulingState = {
  versionScheduling: VersionSchedulingState;
  incrementalScheduling: IncrementalSchedulingState;
};

type FetchIncrementalSchedulingsPayload = {
  versionSchedulingId: FocusVersionScheduling['id'];
};

type DeleteIncrementalSchedulingPayload = {
  schedulingId: FocusScheduling['id'];
  versionSchedulingId: FocusVersionScheduling['id'];
};

const initialState: FocusVersionSchedulingState = {
  versionScheduling: {
    rows: [],
    status: 'idle',
    error: null
  },
  incrementalScheduling: {
    byVersionSchedulingId: {},
    statusByVersionSchedulingId: {},
    errorByVersionSchedulingId: {},
    deleteStatusBySchedulingId: {}
  }
};

export const fetchIncrementalSchedulingsByVersionSchedulingId = createAsyncThunk(
  'focusSchedule/fetchIncrementalSchedulingsByVersionSchedulingId',
  async ({ versionSchedulingId }: FetchIncrementalSchedulingsPayload) => {
    const response = await axios.post(
      '/focus/scheduling/search/',
      {
        version_scheduling_id_filter: [versionSchedulingId]
      },
      { baseURL: '/api/v1.1' }
    );

    const schedulings = response.data?.data ?? [];

    return {
      versionSchedulingId,
      schedulings: [...schedulings].sort((a, b) => new Date(a.window_start).getTime() - new Date(b.window_start).getTime())
    };
  }
);

export const deleteIncrementalScheduling = createAsyncThunk(
  'focusSchedule/deleteIncrementalScheduling',
  async ({ schedulingId, versionSchedulingId }: DeleteIncrementalSchedulingPayload, { dispatch }) => {
    await axios.delete(`/focus/scheduling/${schedulingId}/`, { baseURL: '/api/v1.1' });

    dispatch(fetchIncrementalSchedulingsByVersionSchedulingId({ versionSchedulingId }));

    return { schedulingId };
  }
);

const focusScheduleSlice = createSlice({
  name: 'focusSchedule',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchIncrementalSchedulingsByVersionSchedulingId.pending, (state, action) => {
        const { versionSchedulingId } = action.meta.arg;

        state.incrementalScheduling.statusByVersionSchedulingId[versionSchedulingId] = 'loading';
        state.incrementalScheduling.errorByVersionSchedulingId[versionSchedulingId] = null;
      })
      .addCase(fetchIncrementalSchedulingsByVersionSchedulingId.fulfilled, (state, action) => {
        const { versionSchedulingId, schedulings } = action.payload;

        state.incrementalScheduling.byVersionSchedulingId[versionSchedulingId] = schedulings;
        state.incrementalScheduling.statusByVersionSchedulingId[versionSchedulingId] = 'success';
      })
      .addCase(fetchIncrementalSchedulingsByVersionSchedulingId.rejected, (state, action) => {
        const { versionSchedulingId } = action.meta.arg;

        state.incrementalScheduling.byVersionSchedulingId[versionSchedulingId] = [];
        state.incrementalScheduling.statusByVersionSchedulingId[versionSchedulingId] = 'error';
        state.incrementalScheduling.errorByVersionSchedulingId[versionSchedulingId] =
          action.error.message ?? 'Error fetching incremental schedulings';
      });

    builder
      .addCase(deleteIncrementalScheduling.pending, (state, action) => {
        const { schedulingId } = action.meta.arg;

        state.incrementalScheduling.deleteStatusBySchedulingId[schedulingId] = 'loading';
      })
      .addCase(deleteIncrementalScheduling.fulfilled, (state, action) => {
        const { schedulingId } = action.payload;

        state.incrementalScheduling.deleteStatusBySchedulingId[schedulingId] = 'success';
      })
      .addCase(deleteIncrementalScheduling.rejected, (state, action) => {
        const { schedulingId } = action.meta.arg;

        state.incrementalScheduling.deleteStatusBySchedulingId[schedulingId] = 'error';
      });
  }
});

export default focusScheduleSlice.reducer;
